import type { ComponentChildren } from 'preact';

/**
 * Call-to-action primitives shared across surfaces.
 */

export function CtaPill({
  href,
  children,
  external,
  class: extraClass,
}: {
  href: string;
  children: ComponentChildren;
  external?: boolean;
  class?: string;
}) {
  return (
    <a
      href={href}
      target={external ? '_blank' : undefined}
      rel={external ? 'noreferrer noopener' : undefined}
      class={`inline-flex items-center gap-1.5 rounded-full border border-rule px-3 py-1.5 text-[13px] text-fg-primary transition-colors hover:border-accent-soft hover:bg-accent-glow ${extraClass ?? ''}`}
    >
      {children}
    </a>
  );
}

type ArrowVariant = 'primary' | 'tail' | 'ghost';

const ARROW_BASE =
  'group inline-flex items-baseline gap-1.5 transition-colors';
const ARROW_VARIANT: Record<ArrowVariant, string> = {
  primary: 'font-mono text-[11px] uppercase tracking-[0.14em] text-accent hover:text-fg-primary',
  tail:    'text-[13px] text-fg-muted hover:text-accent',
  ghost:   'text-[13px] text-fg-muted hover:text-fg-primary',
};

/**
 * Link with a trailing arrow glyph that slides 3px on hover.
 */
export function ArrowLink({
  href,
  variant = 'tail',
  external,
  children,
  class: extraClass,
}: {
  href: string;
  variant?: ArrowVariant;
  external?: boolean;
  children: ComponentChildren;
  class?: string;
}) {
  return (
    <a
      href={href}
      target={external ? '_blank' : undefined}
      rel={external ? 'noreferrer noopener' : undefined}
      class={`${ARROW_BASE} ${ARROW_VARIANT[variant]} ${extraClass ?? ''}`}
    >
      <span>{children}</span>
      <span
        aria-hidden="true"
        class="inline-block translate-x-0 transition-transform group-hover:translate-x-[3px]"
      >
        →
      </span>
    </a>
  );
}
